/**
 * Object3DGizmoBinding — the default TransformGizmo binding: drives a plain
 * THREE.Object3D-like target (volumes, groups) by mutating its position /
 * quaternion / scale directly. Appliers recompute from the baseline captured
 * at beginDrag, so cumulative deltas re-apply cleanly.
 *
 * Defaults are overridable per owner:
 *  - `createSnapshot` swaps the plain TRS toggle snapshot for a custom one
 *    (e.g. the box tool's snapshot that re-dispatches persistence events).
 *  - `onCommitted` fires after a moved drag (persistence dispatch).
 *  - `space` overrides the axis-orientation policy (default 'auto').
 */

import * as THREE from 'three';
import { Snapshot } from '../../ToolUndoRedoManager';
import type { GizmoAxis, GizmoFrame, GizmoSpace, TransformGizmoBinding } from './TransformGizmoBinding';

/** Anything the default binding can drive. Highlight hooks are optional
 *  (Volumes have them). */
export interface TransformGizmoTarget extends THREE.Object3D {
  highlight?(): void;
  unHighlight?(): void;
}

export interface Object3DGizmoBindingOptions<T extends TransformGizmoTarget> {
  /** Axis-orientation policy; default 'auto' (object axes in 3D, view in 2D). */
  space?: GizmoSpace;
  /** Undo snapshot factory, called at beginDrag with the PRE-drag target.
   *  Default: a TRS toggle snapshot. */
  createSnapshot?: (target: T) => Snapshot;
  /** Fired from commitDrag after a moved drag (event dispatch etc.). */
  onCommitted?: (target: T) => void;
}

/** Hard floor for per-axis scale so a drag can never collapse the target. */
const MIN_SCALE = 0.01;

interface DragBaseline<T> {
  target: T;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  scale: THREE.Vector3;
  snapshot: Snapshot;
}

export class Object3DGizmoBinding<T extends TransformGizmoTarget = TransformGizmoTarget> implements TransformGizmoBinding {
  private target: T | undefined;
  private baseline: DragBaseline<T> | null = null;

  constructor(private readonly options: Object3DGizmoBindingOptions<T> = {}) {}

  /** Retarget (e.g. hover-follow); undefined clears. */
  setTarget(target: T | undefined): void {
    if (this.target === target) return;
    this.target?.unHighlight?.();
    this.target = target;
    target?.highlight?.();
  }

  getTarget(): T | undefined {
    return this.target;
  }

  // ─── TransformGizmoBinding ──────────────────────────────────────

  getFrame(): GizmoFrame | null {
    if (!this.target || !this.target.visible) return null;
    return {
      position: this.target.getWorldPosition(new THREE.Vector3()),
      quaternion: this.target.getWorldQuaternion(new THREE.Quaternion()),
    };
  }

  getSpace(): GizmoSpace {
    return this.options.space ?? 'auto';
  }

  getEnabledHandles(): ReadonlyArray<string> | null {
    return null;
  }

  beginDrag(): void {
    const target = this.target;
    if (!target) return;
    this.baseline = {
      target,
      position: target.position.clone(),
      quaternion: target.quaternion.clone(),
      scale: target.scale.clone(),
      snapshot: this.options.createSnapshot?.(target) ?? Object3DGizmoBinding.createTrsToggleSnapshot(target),
    };
  }

  applyTranslation(worldDelta: THREE.Vector3, _override: boolean): void {
    if (!this.baseline) return;
    // Targets sit in the scene root (world == parent space) — the same
    // assumption the previous direct-drag gizmo made.
    this.baseline.target.position.copy(this.baseline.position).add(worldDelta);
  }

  applyRotation(worldAxis: THREE.Vector3, angle: number, _override: boolean): void {
    if (!this.baseline) return;
    const spin = new THREE.Quaternion().setFromAxisAngle(worldAxis, angle);
    this.baseline.target.quaternion.copy(this.baseline.quaternion).premultiply(spin);
  }

  applyScale(axes: ReadonlyArray<GizmoAxis>, factor: number, _override: boolean): void {
    if (!this.baseline) return;
    for (const axis of axes) {
      this.baseline.target.scale[axis] = Math.max(MIN_SCALE, this.baseline.scale[axis] * factor);
    }
  }

  commitDrag(): Snapshot | null {
    const baseline = this.baseline;
    this.baseline = null;
    if (!baseline) return null;
    this.options.onCommitted?.(baseline.target);
    return baseline.snapshot;
  }

  cancelDrag(): void {
    this.baseline = null;
  }

  /** Default undo snapshot: toggles the full TRS between the pre-drag state
   *  and whatever the target holds at restore time. */
  private static createTrsToggleSnapshot(target: THREE.Object3D): Snapshot {
    let stored = { position: target.position.clone(), quaternion: target.quaternion.clone(), scale: target.scale.clone() };
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        const current = { position: target.position.clone(), quaternion: target.quaternion.clone(), scale: target.scale.clone() };
        target.position.copy(stored.position);
        target.quaternion.copy(stored.quaternion);
        target.scale.copy(stored.scale);
        stored = current;
        return snapshot;
      },
    };
    return snapshot;
  }
}
